<script lang="ts">
  // Continuity module — the admin-only Playbook: a cheat sheet of
  // management principles, re-read more than it's edited. Reading-first:
  // bold title + plain-paragraph body per entry, grouped under a section
  // header, matching the app's existing long-form typography (see
  // PolicyPage.svelte's .tym-card / whitespace-pre-wrap body pattern —
  // Playbook bodies are plain text, not markdown, so no renderMarkdown
  // here, just newline-preserving paragraphs).
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronUp, ChevronDown, Pencil, Plus } from 'lucide-svelte';
  import { playbookApi } from '$lib/api/playbook';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import type { PlaybookEntry } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({ queryKey: ['continuity-playbook'], queryFn: () => playbookApi.list() }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['continuity-playbook'] });
  }

  // Group by section, section order = first appearance in the (already
  // position-ordered) list — robust to entries of different sections
  // having interleaved position numbers (e.g. after a reorder or a newly
  // appended principle), since bucketing is by section value, not by
  // contiguous position ranges.
  const sections = $derived.by(() => {
    const entries = query.data || [];
    const order: string[] = [];
    const bySection = new Map<string, PlaybookEntry[]>();
    for (const e of entries) {
      if (!bySection.has(e.section)) {
        bySection.set(e.section, []);
        order.push(e.section);
      }
      bySection.get(e.section)!.push(e);
    }
    return order.map((name) => ({ name, entries: bySection.get(name)! }));
  });

  // ---- Reorder (up/down within a section) ----
  function sameSectionNeighbor(entry: PlaybookEntry, direction: -1 | 1): PlaybookEntry | null {
    const siblings = (query.data || []).filter((e) => e.section === entry.section);
    const idx = siblings.findIndex((e) => e.id === entry.id);
    const neighbor = siblings[idx + direction];
    return neighbor ?? null;
  }

  async function moveEntry(entry: PlaybookEntry, direction: -1 | 1) {
    const neighbor = sameSectionNeighbor(entry, direction);
    if (!neighbor) return;
    try {
      await playbookApi.update(entry.id, { position: neighbor.position });
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // ---- Inline edit ----
  let editingId = $state<number | null>(null);
  let editForm = $state({ section: '', title: '', body: '' });
  let editError = $state('');
  let savingEdit = $state(false);

  function startEdit(entry: PlaybookEntry) {
    editingId = entry.id;
    editForm = { section: entry.section, title: entry.title, body: entry.body };
    editError = '';
  }

  async function saveEdit() {
    if (editingId === null) return;
    if (!editForm.section.trim() || !editForm.title.trim()) {
      editError = 'Section and title are required.';
      return;
    }
    savingEdit = true;
    editError = '';
    try {
      await playbookApi.update(editingId, editForm);
      editingId = null;
      refresh();
      toast.success('Saved.');
    } catch (err) {
      editError = getErrorMessage(err);
    } finally {
      savingEdit = false;
    }
  }

  // ---- Delete: two-step armed confirm (no native confirm(), see commit 8ea82e3) ----
  let confirmingDeleteId = $state<number | null>(null);
  let confirmDeleteTimer: ReturnType<typeof setTimeout> | undefined;

  async function deleteEntry(entry: PlaybookEntry) {
    if (confirmingDeleteId !== entry.id) {
      confirmingDeleteId = entry.id;
      clearTimeout(confirmDeleteTimer);
      confirmDeleteTimer = setTimeout(() => (confirmingDeleteId = null), 4000);
      return;
    }
    confirmingDeleteId = null;
    clearTimeout(confirmDeleteTimer);
    try {
      await playbookApi.remove(entry.id);
      refresh();
      toast.success('Removed.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // ---- Add (a principle to an existing section, or a whole new section) ----
  let addForm = $state<{ section: string; title: string; body: string; newSection: boolean } | null>(null);
  let addError = $state('');
  let savingAdd = $state(false);

  function openAddPrinciple(section: string) {
    addForm = { section, title: '', body: '', newSection: false };
    addError = '';
  }

  function openAddSection() {
    addForm = { section: '', title: '', body: '', newSection: true };
    addError = '';
  }

  async function submitAdd() {
    if (!addForm) return;
    if (!addForm.section.trim() || !addForm.title.trim()) {
      addError = 'Section and title are required.';
      return;
    }
    savingAdd = true;
    addError = '';
    try {
      await playbookApi.create({ section: addForm.section, title: addForm.title, body: addForm.body });
      addForm = null;
      refresh();
      toast.success('Added.');
    } catch (err) {
      addError = getErrorMessage(err);
    } finally {
      savingAdd = false;
    }
  }
</script>

<div class="tym-page">
  <div class="mb-4 flex items-center justify-between">
    <p class="tym-section-sub">Management principles worth not re-learning the hard way.</p>
    <button type="button" class="btn-secondary" onclick={openAddSection}>
      <Plus class="h-4 w-4" /> Add section
    </button>
  </div>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load the playbook.</div>
  {:else}
    <div class="space-y-8">
      {#each sections as section (section.name)}
        <div>
          <h2 class="mb-3 font-heading text-lg font-bold text-navy">{section.name}</h2>
          <div class="space-y-4">
            {#each section.entries as entry, i (entry.id)}
              <div class="tym-card p-5">
                {#if editingId === entry.id}
                  <div class="space-y-3">
                    <div>
                      <div class="label">Section</div>
                      <input class="input" bind:value={editForm.section} />
                    </div>
                    <div>
                      <div class="label">Title</div>
                      <input class="input" bind:value={editForm.title} />
                    </div>
                    <div>
                      <div class="label">Body</div>
                      <textarea class="input" rows="4" bind:value={editForm.body}></textarea>
                    </div>
                    {#if editError}<div class="tym-error-state">{editError}</div>{/if}
                    <div class="flex justify-end gap-2">
                      <button type="button" class="btn-secondary" onclick={() => (editingId = null)}>Cancel</button>
                      <button type="button" class="btn-primary" disabled={savingEdit} onclick={saveEdit}>
                        {savingEdit ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                {:else}
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <p class="font-semibold text-navy">{entry.title}</p>
                      {#if entry.body}
                        <p class="mt-1.5 whitespace-pre-wrap text-[13.5px] text-toyama-heavy-carbon">{entry.body}</p>
                      {/if}
                    </div>
                    <div class="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        class="btn-secondary !px-2"
                        disabled={i === 0}
                        onclick={() => moveEntry(entry, -1)}
                        aria-label="Move up"
                      >
                        <ChevronUp class="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        class="btn-secondary !px-2"
                        disabled={i === section.entries.length - 1}
                        onclick={() => moveEntry(entry, 1)}
                        aria-label="Move down"
                      >
                        <ChevronDown class="h-3.5 w-3.5" />
                      </button>
                      <button type="button" class="btn-secondary !px-2" onclick={() => startEdit(entry)} aria-label="Edit">
                        <Pencil class="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        class={confirmingDeleteId === entry.id ? 'btn-danger-solid !px-2' : 'btn-secondary !px-2'}
                        onclick={() => deleteEntry(entry)}
                        onblur={() => (confirmingDeleteId = null)}
                      >
                        {confirmingDeleteId === entry.id ? 'Confirm?' : 'Delete'}
                      </button>
                    </div>
                  </div>
                {/if}
              </div>
            {/each}

            {#if addForm && !addForm.newSection && addForm.section === section.name}
              <div class="tym-card space-y-3 p-5">
                <div>
                  <div class="label">Title</div>
                  <input class="input" bind:value={addForm.title} />
                </div>
                <div>
                  <div class="label">Body</div>
                  <textarea class="input" rows="3" bind:value={addForm.body}></textarea>
                </div>
                {#if addError}<div class="tym-error-state">{addError}</div>{/if}
                <div class="flex justify-end gap-2">
                  <button type="button" class="btn-secondary" onclick={() => (addForm = null)}>Cancel</button>
                  <button type="button" class="btn-primary" disabled={savingAdd} onclick={submitAdd}>
                    {savingAdd ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            {:else}
              <button type="button" class="btn-secondary" onclick={() => openAddPrinciple(section.name)}>
                <Plus class="h-3.5 w-3.5" /> Add principle
              </button>
            {/if}
          </div>
        </div>
      {/each}

      {#if addForm?.newSection}
        <div class="tym-card space-y-3 p-5">
          <p class="tym-section-title">New section</p>
          <div>
            <div class="label">Section name</div>
            <input class="input" bind:value={addForm.section} />
          </div>
          <div>
            <div class="label">Title</div>
            <input class="input" bind:value={addForm.title} />
          </div>
          <div>
            <div class="label">Body</div>
            <textarea class="input" rows="3" bind:value={addForm.body}></textarea>
          </div>
          {#if addError}<div class="tym-error-state">{addError}</div>{/if}
          <div class="flex justify-end gap-2">
            <button type="button" class="btn-secondary" onclick={() => (addForm = null)}>Cancel</button>
            <button type="button" class="btn-primary" disabled={savingAdd} onclick={submitAdd}>
              {savingAdd ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>
